/* eslint-disable no-console */
import { get, isEmpty, each } from 'lodash';
import Mixpanel from './services/mixpanel';
import GoogleAnalytics from './services/ga';
import FacebookPixel from './services/fb';
import AnalyticsEvent from './events/event';

export default function install(Vue) {
  if (install.installed) {
    return;
  }

  install.installed = true;
  Vue.analytics = new Analytics();
}

export const debugEnabled = get(window, 'TIS.environment', 'local') !== 'production';
export const analyticsCategory = (identifier) => get(window, ['TIS', 'analytics', 'validCategories', identifier]);

export const websiteCategory = analyticsCategory('website');
export const blogCategory = analyticsCategory('blog');
export const helpdeskCategory = analyticsCategory('helpdesk');
export const clientDashboardCategory = analyticsCategory('clientdash');
export const signUpCategory = analyticsCategory('signup');
export const placeOrderCategory = analyticsCategory('placeorder');

export class Analytics {
  constructor() {
    this.mixpanel = this.mixpanel ? this.mixpanel : new Mixpanel();
    this.ga = this.ga ? this.ga : new GoogleAnalytics();
    this.fb = this.fb ? this.fb : new FacebookPixel();
  }

  get enabled() {
    return this.mixpanel.enabled || this.ga.enabled || this.fb.enabled;
  }

  get debugEnabled() {
    return debugEnabled;
  }

  // this fires whenever inertia receives new props (aka a page view)
  processWebsiteInertiaRequest(inertiaProps) {
    // Track page view but wait a bit to allow vue-headful to update the document.title
    setTimeout(() => {
      this.trackWebsitePageView(inertiaProps);
    }, 100);

    this.trackServerEvents(inertiaProps);

    return inertiaProps;
  }

  processClientDashInertiaRequest(inertiaProps) {
    this.trackServerEvents(inertiaProps);

    return inertiaProps;
  }

  processHelpdeskInertiaRequest(inertiaProps) {
    this.trackServerEvents(inertiaProps);

    return inertiaProps;
  }

  trackServerEvents(inertiaProps) {
    if (inertiaProps && !isEmpty(inertiaProps.analytics)) {
      each(inertiaProps.analytics, (analyticsEventData) => {
        this.trackEvent(new AnalyticsEvent(analyticsEventData));
      });
    }
  }

  trackWebsitePageView(inertiaProps) {
    if (!this.enabled) {
      return;
    }

    this.mixpanel.trackWebsitePageView(inertiaProps);
    // note: fb + ga do this automatically
  }

  trackClientDashboardPageView(pageName) {
    if (!this.enabled) {
      return;
    }

    this.mixpanel.trackClientDashboardPageView(pageName);
    // note: fb + ga do this automatically
  }

  trackWebsiteEvent(name, services = ['mixpanel', 'fb', 'ga'], data = {}, fbTrackMethod) {
    this._trackCategoryEvent(websiteCategory, name, data, services, fbTrackMethod);
  }

  trackBlogEvent(name, services = ['mixpanel'], data = {}, fbTrackMethod) {
    this._trackCategoryEvent(blogCategory, name, data, services, fbTrackMethod);
  }

  trackHelpdeskEvent(name, services = ['mixpanel'], data = {}, fbTrackMethod) {
    this._trackCategoryEvent(helpdeskCategory, name, data, services, fbTrackMethod);
  }

  trackClientDashboardEvent(name, services = ['mixpanel'], data = {}, fbTrackMethod) {
    this._trackCategoryEvent(clientDashboardCategory, name, data, services, fbTrackMethod);
  }

  trackEventFromServer(eventProps) {
    this.trackEvent(new AnalyticsEvent(eventProps));
  }

  trackEvent(event) { // event must be an instance of AnalyticsEvent
    if (!this.enabled) {
      return;
    }

    if (!event instanceof AnalyticsEvent) {
      console.error('analytics trackEvent method can only receive events that are instances of AnalyticsEvent', event);
      return;
    }

    if (this.debugEnabled) {
      console.info(`analytics tracking event: ${event.category}: ${event.name}`, event.data);
    }

    each(event.services, (service) => {
      if (this[service] && typeof this[service] !== 'undefined' && typeof this[service]._track !== 'undefined') {
        this[service]._track(event);
      }
    });

    if (event.shouldReIdentifyUser && event.identity) {
      this.reIdentify(event.identity);
    }
  }

  alias(analyticsIdentity) {
    if (!this.enabled) {
      return;
    }

    this.mixpanel.alias(get(analyticsIdentity, 'clientId'));
    this.fb.reIdentify(analyticsIdentity);
  }

  reIdentify(analyticsIdentity) {
    if (!this.enabled) {
      return;
    }

    this.mixpanel.reIdentify(analyticsIdentity);
    this.fb.reIdentify(analyticsIdentity);
  }

  _trackCategoryEvent(category, name, data, services, fbTrackMethod) {
    if (!this.enabled) {
      return;
    }

    const event = new AnalyticsEvent({
      category,
      name,
      data,
      services,
      fbTrackMethod,
    });

    if (isEmpty(event)) {
      return;
    }

    this.trackEvent(event);
  }
}
